import type { EmailOtpType, Session, User } from '@supabase/supabase-js'
import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { supabase } from '@/supabase';
import type { Employee, Profile } from "~/types";
const crypto = Number(import.meta.env.VITE_CRYPTO);

export const useAuthStore = defineStore("authStore", () => {
  const isRedirect = ref<boolean>(false);
  const isReload = ref<boolean>(false);

  /* Private variables */
  const _active_emp = ref<Employee>({} as Employee);
  const _isPending = ref<boolean>(false);

  /* Public variables */
  const isDemo = ref<boolean>(false);
  const session = ref<Session>({} as Session);
  const profile = ref<Profile>({} as Profile);
  const redeemedCodes = ref<any>([]);
  const active_emp = computed(() => _active_emp.value);
  const active_emp_id = computed({
    get() {
        console.log(`active_emp_id get:`, profile.value?.active_emp_id, 0);
        return profile.value?.active_emp_id || 0;
    },
    set(val) {
        _active_emp.value = redeemedCodes.value.find((r: any) => r.id === val) || {} as Employee;
        if (!_active_emp.value) redeemedCodes.value.length > 0 ? redeemedCodes.value[0] : {} as Employee;
        
        profile.value.active_emp_id = _active_emp.value?.id || 0;
        currentLocationId.value = _active_emp.value?.location?.id || 0;

        console.log(`active_emp_id set:`, _active_emp.value, profile.value, currentLocationId.value);
        updateProfile(profile.value);
    }
  });
  const currentLocationId = ref<number>(0);
  const currency = computed(() => { return '$'; });
  const is12Hr = computed({
    get() { return !profile.value || profile.value.is12Hr },
    set(val) {
      profile.value.is12Hr = val;
      updateProfile(profile.value);
    }
  });
  const is15Mn = computed({
    get() { return !profile.value || profile.value.is15Mn },
    set(val) {
      profile.value.is15Mn = val;
      updateProfile(profile.value);
    }
  });
  const isDark = computed({
    get() { return !!profile.value && profile.value.isDark },
    set(val) {
      profile.value.isDark = val;
      updateProfile(profile.value);
    }
  });
  const isLoggedIn = computed(() => { return !!session.value?.user; });
  const isSuperAdmin = computed(() => _active_emp.value?.role?.id === 1);
  const isAdmin = computed(() => _active_emp.value?.role?.id <= 2);
  const isManager = computed(() => _active_emp.value?.role?.id <= 3);
  const isMember = computed(() => _active_emp.value?.role?.id <= 4);
  const isPending = computed(() => _isPending.value);

  const showAmPm = ref<boolean>(true);
  const showCost = ref<boolean>(isAdmin.value);
  const showTimeSpan = ref<boolean>(isManager.value);

  const isCostShown = computed(() => isAdmin.value && showCost.value);
  const isTimeSpanShown = computed(() => isManager.value && showTimeSpan.value);

  const allowedMinutes = (m: number) => { return m % (is15Mn.value ? 15 : 1) === 0; };

  async function approveEmailChange(): Promise<string> {
    const searchParams = new URLSearchParams(window.location.search);
    const token_hash = searchParams.get('token_hash');
    const type = searchParams.get('type') as EmailOtpType | null;
    const next = searchParams.get('next') ?? '/';

    if (token_hash && type) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token_hash,
        type: type
      });

      if (error) {
        return (error.message as string).replace('Email', 'E-mail');
      }
      
      navigateTo(next);
      return '';
    }

    return 'Invalid or expired change E-mail request.';
  }

  async function goToPasswordResetPage(): Promise<string> {
    const searchParams = new URLSearchParams(window.location.search);
    const token_hash = searchParams.get('token_hash');
    const type = searchParams.get('type') as EmailOtpType | null;
    const next = searchParams.get('next') ?? '/';

    if (token_hash && type) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token_hash,
        type: type
      });

      if (error) {
        return error.message as string;
      }
      
      navigateTo(next);
      return '';
    }

    return 'Invalid or expired password reset request.';
  }

  async function changeEmail(email: string): Promise<string> {
    if (_isPending.value) return '';

    _isPending.value = true;
    let msg = '';

    const { error } = await supabase.auth.updateUser({ email: email });
    if (error) {
      msg = error.message as string;
    }

    _isPending.value = false;
    return msg;
  }

  async function changePassword(password: string): Promise<string> {
    if (_isPending.value) return '';

    _isPending.value = true;
    let msg = '';

    const { error } = await supabase.auth.updateUser({ password: password });
    if (error) {
      msg = error.message as string;
    }

    _isPending.value = false;
    return msg;
  }

  async function fetchProfile(): Promise<string> {
    console.log(`fetchProfile() - profile.value:`, profile.value);
    if (profile.value?.active_emp_id) return '';

    console.log('fetchProfile() - loadSession()');
    let msg = await loadSession();
    if (msg !== '') return msg;

    console.log('session.value:', session.value)

    if (!session.value) {
      return '';
    }
    _isPending.value = true;

    console.log(`fetchProfile() - session.value.user.id: ${session.value.user.id}`);

    const { data, error } = 
    isDemo.value
    ? await supabase
      .from('profile_demo')
      .select('*, role:role_id (*)')
      .eq('id', session.value.user.id)
      .limit(1)
      .single()
    : await supabase
      .from('profile')
      .select('*, role:role_id (*)')
      .eq('id', session.value.user.id)
      .limit(1)
      .single();

    console.log('info fetch:', active_emp.value, active_emp_id.value, profile.value);
    if (error) {
      console.log("error()", error)
      msg = error.message as string;
    }
    else if (data) {
      console.log("data()", data)
      profile.value = data;
    }

    _isPending.value = false;
    return msg;
  }

  async function fetchLinkCodes(): Promise<string> {
    console.log(`fetchLinkCodes() - profile.value.account_id: ${profile.value.account_id}`);
    if (!profile.value.account_id) { return 'Error:  Please try again'; }
    console.log(`fetchLinkCodes() - profile.value.account_id: ${profile.value.account_id}`);
    _isPending.value = true;

    let msg = '';

    const { data, error } =
    isDemo.value
    ? await supabase
      .from('employee_demo')
      .select('id, fname, lname, wage, location_demo:location_id(*), department_demo:dept_id(*), code, profile_demo:account_id(email, phone), role:role_id(*), start, end, sort_order')
      .eq('account_id', profile.value.account_id)
      .is('active', true)
    : await supabase
      .from('employee')
      .select('id, fname, lname, wage, location:location_id(*), department:dept_id(*), code, profile:account_id(email, phone), role:role_id(*), start, end, sort_order')
      .eq('account_id', profile.value.account_id)
      .is('active', true);

    if (error) {
      msg = error.message as string;
    }
    else if (data) {
      redeemedCodes.value = data.map(d => {
        return {
          ...d,
          location: isDemo.value ? d.location_demo : d.location,
          department: isDemo.value ? d.department_demo : d.department,
          profile: isDemo.value ? d.profile_demo : d.profile, // Fix incorrect mapping to location
          wage: Number(`0x${d.wage}`) / 100 - (Number(`0x${d.code.substring(1, 5)}`) * crypto),
          full_name: `${d.fname} ${d.lname}`
        }
      });
      
    console.log('info link:', active_emp.value, active_emp_id.value, profile.value);
      if (active_emp_id.value === 0 && redeemedCodes.value.length > 0) {
        active_emp_id.value = redeemedCodes.value[0].id;
      }
      else if (active_emp_id.value === 0) {
        active_emp_id.value = profile.value.active_emp_id;
      }
    }

    _isPending.value = false;
    return msg;
  }

  async function updateProfile(item: any): Promise<string> {
    console.log("updateProfile()", item)
    if (!profile.value || _isPending.value) return 'Error:  Please try again.';
    
    _isPending.value = true;

    let msg = '';

    const { data, error } = await supabase
    .from(isDemo.value ? 'profile_demo' : 'profile')
    .update([
      {
          fname: item.fname,
          lname: item.lname,
          phone: item.phone,
          is12Hr: item.is12Hr,
          is15Mn: item.is15Mn,
          isDark: item.isDark,
          lang_id: item.lang_id,
          active_emp_id: item.active_emp_id || active_emp_id.value
      }
    ])
    .eq('account_id', item.account_id)
    .select();

    console.log('info update:', active_emp.value, active_emp_id.value, profile.value);
    if (error) {
      console.log("error()", error)
      msg = error.message as string;
    }
    else if (data) {
      console.log("data()", data)
      if (data.length > 0) profile.value = data[0];
    }

    _isPending.value = false;
    return msg;
  }
  
  async function getUser(): Promise<User> {
    await loadSession();
    return session.value?.user || {} as User;
  }

  async function loadSession(): Promise<string> {
    console.log('loadSession()');

    if (isDemo.value) {
      session.value = {
        user: {
          id: '54bfecd6-4877-4b74-9bf1-909f20a25378'
        }
      };
      return '';
    }

    _isPending.value = true;

    let msg = '';

    console.log('await supabase.auth.getSession()');
    const { data, error } = await supabase.auth.getSession();

    if (error) {
        console.log('error()', error);
      msg = error.message as string;
      session.value = null;
    }
    else if (data) {
        console.log('data()', data);
      if (!data.session) msg = 'no session found';
      else session.value = data.session;
      console.log(`data.session:`, data.session);
    }

    _isPending.value = false;
    return msg;
  }

  async function logIn(email: string, password: string): Promise<string> {
    console.log('logIn()');
    if (_isPending.value) return '';
    _isPending.value = true;

    let msg = '';

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password
    });

    if (error) {
      msg = error.message
      if (msg === 'Invalid login credentials') msg = 'Invalid E-mail or password';
      else if (msg === 'Email not confirmed') msg = 'E-mail not yet confirmed';
    }
    else if (data) {
      session.value = data;
    }

    _isPending.value = false;
    return msg;
  }

  async function logOut(): Promise<boolean> {
    _isPending.value = true;
    let success = true;

    console.log('logout 0');
    const { error } = await supabase.auth.signOut();
    console.log('logout 1');

    if (error) {
      console.log('logout 2');
      success = false;
    }
    else {
      console.log('logout 3');
      resetStores();
      console.log('logout 4');
    }

    console.log('logout 5');
    localStorage.clear();
    console.log('logout 6');

    _isPending.value = false;
    return success;
  }

  async function recoverPassword(email: string): Promise<string> {
    if (_isPending.value) return '';
    _isPending.value = true;
    let msg = '';

    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: '/password-reset'});

    if (error) {
      msg = error.message as string;
    }

    _isPending.value = false;
    return msg;
  }

  function resetStores(): void {
    session.value = null;
    profile.value = null;
  }

  async function signUp(email: string, password: string): Promise<string> {
    if (_isPending.value) return '';
    _isPending.value = true;
    let msg = '';

    const { data, error } = await supabase.auth.signUp({ email: email, password: password });

    if (data.user && data.user.identities && data.user.identities.length === 0) {
      msg = 'E-mail address already in use.';
    }
    else if (error) {
      console.log(error)
      msg = error.message as string;
    }
    else if (data) {
      session.value = data;
    }

    _isPending.value = false;
    return msg;
  }

  return {
    allowedMinutes, approveEmailChange, changeEmail, changePassword, fetchProfile, fetchLinkCodes, getUser, loadSession, logIn, logOut,
    recoverPassword, resetStores, signUp, updateProfile, goToPasswordResetPage,
    active_emp, active_emp_id, currentLocationId, currency, is12Hr, is15Mn, isCostShown, isDark, isDemo, isTimeSpanShown, isSuperAdmin, isAdmin, isManager, isMember,
    isLoggedIn, isPending, isRedirect, isReload, redeemedCodes, showAmPm, showCost, showTimeSpan, profile
  };
},
{
  persist: {
    omit: ['_isDark', 'profile', '_isPending', 'redeemedCodes', 'isDemo']
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}